import React, { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  X, User, Briefcase, Calendar, FileText, MessageSquare, Brain,
  ExternalLink, Mail, Phone, Clock, DollarSign, Save, Linkedin, GraduationCap, Building2
} from 'lucide-react';

export default function ApplicantDetail({ app, refData, apiCall, onClose, onRefresh, stages, stageMap, badgeStyle }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    status: app.status,
    notice_period_text: app.notice_period_text || '',
    expected_salary_min: app.expected_salary_min ?? '',
    expected_salary_max: app.expected_salary_max ?? '',
    expected_salary_currency: app.expected_salary_currency || 'USD',
    primary_manager_person_id: app.primary_manager_person_id || '',
    secondary_manager_person_id: app.secondary_manager_person_id || '',
    jira_url: app.jira_url || '',
    notes: app.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiCall('PUT', `/recruitment/applications/${app.application_id}`, {
        ...form,
        expected_salary_min: form.expected_salary_min ? Number(form.expected_salary_min) : null,
        expected_salary_max: form.expected_salary_max ? Number(form.expected_salary_max) : null,
      });
      toast.success('Updated');
      setEditMode(false);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiCall('PUT', `/recruitment/applications/${app.application_id}`, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const managerName = (pid) => {
    if (!pid || !refData?.members) return 'None';
    const m = refData.members.find(x => x.person_id === pid);
    return m?.name || 'Unknown';
  };

  const person = app.person || {};
  const meetings = app.meetings || [];
  const files = app.files || [];
  const events = app.events || [];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="applicant-detail-panel">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="w-[640px] bg-background border-l shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">{app.candidate_name || 'Unknown Candidate'}</h2>
            <p className="text-sm text-muted-foreground">{app.job_title || 'No role'} {app.department_name ? `· ${app.department_name}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={app.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[180px] h-8" data-testid="detail-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stages.map(s => (
                  <SelectItem key={s.name} value={s.name}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      <span>{s.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} data-testid="detail-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-5">
            <Tabs defaultValue="overview">
              <TabsList className="mb-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="meetings">Meetings ({meetings.length})</TabsTrigger>
                <TabsTrigger value="files">Files ({files.length})</TabsTrigger>
                <TabsTrigger value="activity">Activity ({events.length})</TabsTrigger>
              </TabsList>

              {/* ── Overview ── */}
              <TabsContent value="overview" className="space-y-5">
                {/* Identity */}
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Identity</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name</span>
                      <p className="font-medium">{app.candidate_name || '--'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email</span>
                      <p className="font-medium flex items-center gap-1">
                        {app.candidate_email || '--'}
                        {app.candidate_email && <Mail className="w-3 h-3 text-muted-foreground" />}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Person ID</span>
                      <p className="font-mono text-xs">{app.person_id || '--'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Source</span>
                      <p>{app.source_type || '--'}</p>
                    </div>
                    {person.primary_phone && (
                      <div>
                        <span className="text-muted-foreground">Phone</span>
                        <p className="flex items-center gap-1"><Phone className="w-3 h-3" />{person.primary_phone}</p>
                      </div>
                    )}
                  </div>
                </Card>

                {/* LinkedIn Profile (shown if any LinkedIn data exists) */}
                {(app.linkedin_profile_url || app.linkedin_headline || app.linkedin_experience || app.linkedin_education) && (
                  <Card className="p-4" data-testid="linkedin-profile-card">
                    <div className="flex items-center gap-2 mb-3">
                      <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                      <span className="text-sm font-medium">LinkedIn Profile</span>
                      {app.linkedin_profile_url && (
                        <a href={app.linkedin_profile_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0A66C2] hover:underline flex items-center gap-0.5 ml-auto" data-testid="linkedin-profile-link">
                          View Profile <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    <div className="space-y-3 text-sm">
                      {app.linkedin_headline && (
                        <div>
                          <span className="text-muted-foreground text-xs">Headline</span>
                          <p className="font-medium">{app.linkedin_headline}</p>
                        </div>
                      )}
                      {app.linkedin_filter_status && (
                        <div>
                          <span className="text-muted-foreground text-xs">LinkedIn Status</span>
                          <Badge variant="outline" className="ml-2 text-xs">{app.linkedin_filter_status}</Badge>
                        </div>
                      )}
                      {app.linkedin_experience && Array.isArray(app.linkedin_experience) && app.linkedin_experience.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground text-xs font-medium">Experience</span>
                          </div>
                          <div className="space-y-2 pl-5">
                            {app.linkedin_experience.map((exp, i) => (
                              <div key={i} className="border-l-2 border-muted pl-3 py-0.5">
                                <p className="font-medium text-xs">{exp.title || exp.role || '--'}</p>
                                <p className="text-xs text-muted-foreground">{exp.company || '--'}{exp.duration ? ` · ${exp.duration}` : ''}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {app.linkedin_education && Array.isArray(app.linkedin_education) && app.linkedin_education.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <GraduationCap className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground text-xs font-medium">Education</span>
                          </div>
                          <div className="space-y-2 pl-5">
                            {app.linkedin_education.map((edu, i) => (
                              <div key={i} className="border-l-2 border-muted pl-3 py-0.5">
                                <p className="font-medium text-xs">{edu.school || edu.institution || '--'}</p>
                                <p className="text-xs text-muted-foreground">{edu.degree || '--'}{edu.field ? ` in ${edu.field}` : ''}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Application Details */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Application</span>
                    </div>
                    {!editMode && (
                      <Button variant="ghost" size="sm" onClick={() => setEditMode(true)} data-testid="edit-app-btn">Edit</Button>
                    )}
                  </div>
                  {editMode ? (
                    <div className="grid gap-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Salary Min</Label>
                          <Input type="number" value={form.expected_salary_min} onChange={e => set('expected_salary_min', e.target.value)} className="h-8" />
                        </div>
                        <div>
                          <Label className="text-xs">Salary Max</Label>
                          <Input type="number" value={form.expected_salary_max} onChange={e => set('expected_salary_max', e.target.value)} className="h-8" />
                        </div>
                        <div>
                          <Label className="text-xs">Currency</Label>
                          <Input value={form.expected_salary_currency} onChange={e => set('expected_salary_currency', e.target.value)} className="h-8" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Notice Period</Label>
                        <Input value={form.notice_period_text} onChange={e => set('notice_period_text', e.target.value)} className="h-8" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Primary Manager</Label>
                          <Select value={form.primary_manager_person_id || '_none'} onValueChange={v => set('primary_manager_person_id', v === '_none' ? '' : v)}>
                            <SelectTrigger className="h-8"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">None</SelectItem>
                              {(refData?.members || []).map(m => <SelectItem key={m.person_id} value={m.person_id}>{m.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Secondary Manager</Label>
                          <Select value={form.secondary_manager_person_id || '_none'} onValueChange={v => set('secondary_manager_person_id', v === '_none' ? '' : v)}>
                            <SelectTrigger className="h-8"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">None</SelectItem>
                              {(refData?.members || []).map(m => <SelectItem key={m.person_id} value={m.person_id}>{m.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Jira Link</Label>
                        <Input value={form.jira_url} onChange={e => set('jira_url', e.target.value)} className="h-8" placeholder="https://..." />
                      </div>
                      <div>
                        <Label className="text-xs">Notes</Label>
                        <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-detail-btn">
                          <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Applied Role</span>
                        <p className="font-medium">{app.job_title || '--'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Department</span>
                        <p>{app.department_name || '--'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expected Salary</span>
                        <p className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {app.expected_salary_min || app.expected_salary_max ?
                            `${app.expected_salary_currency || ''} ${app.expected_salary_min ? (app.expected_salary_min/1000).toFixed(0)+'k' : ''}${app.expected_salary_max ? '–'+(app.expected_salary_max/1000).toFixed(0)+'k' : ''}` : '--'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Notice Period</span>
                        <p className="flex items-center gap-1"><Clock className="w-3 h-3" />{app.notice_period_text || '--'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Primary Manager</span>
                        <p>{managerName(app.primary_manager_person_id)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Secondary Manager</span>
                        <p>{managerName(app.secondary_manager_person_id)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Applied Date</span>
                        <p>{app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '--'}</p>
                      </div>
                      {app.jira_url && (
                        <div>
                          <span className="text-muted-foreground">Jira</span>
                          <p><a href={app.jira_url} target="_blank" rel="noreferrer" className="text-blue-600 flex items-center gap-1 hover:underline">Open <ExternalLink className="w-3 h-3" /></a></p>
                        </div>
                      )}
                      {app.notes && (
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Notes</span>
                          <p className="whitespace-pre-wrap text-sm">{app.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>

                {/* AI Summary */}
                {app.summary_ai && (
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Brain className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">AI Summary</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{app.summary_ai}</p>
                  </Card>
                )}
              </TabsContent>

              {/* ── Meetings ── */}
              <TabsContent value="meetings" className="space-y-3">
                {meetings.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No meetings scheduled</p>
                ) : meetings.map(m => (
                  <Card key={m.application_meeting_id} className="p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium">{m.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {m.start_time ? new Date(m.start_time).toLocaleString() : ''} – {m.end_time ? new Date(m.end_time).toLocaleTimeString() : ''}
                        </p>
                        {m.notes && <p className="text-xs mt-1">{m.notes}</p>}
                      </div>
                      {m.meeting_url && (
                        <a href={m.meeting_url} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3 mr-1" />Join</Button>
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
              </TabsContent>

              {/* ── Files ── */}
              <TabsContent value="files" className="space-y-3">
                {files.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No files attached</p>
                ) : files.map(f => (
                  <Card key={f.file_id} className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{f.filename}</p>
                        <p className="text-xs text-muted-foreground">{f.file_type} · {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                    {f.source_url && (
                      <a href={f.source_url} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                    )}
                  </Card>
                ))}
              </TabsContent>

              {/* ── Activity ── */}
              <TabsContent value="activity">
                {events.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No activity yet</p>
                ) : (
                  <div className="relative pl-4 border-l-2 border-muted space-y-4">
                    {events.map(ev => (
                      <div key={ev.event_id} className="relative">
                        <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-border" />
                        <div>
                          <p className="text-sm font-medium">{ev.event_type.replace(/_/g, ' ')}</p>
                          {ev.payload_json && (
                            <p className="text-xs text-muted-foreground">
                              {ev.payload_json.old_status && `${ev.payload_json.old_status} → `}{ev.payload_json.new_status || ''}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">{ev.received_at ? new Date(ev.received_at).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
